export enum LogLevel {
	fatal = 1,
	error = 2,
	warn = 3,
	info = 4,
	debug = 5,
	trace = 6,
}

export type LogType = "INFO" | "FLOW" | "METRIC" | "NETWORK" | "UI";

// Closed recursive type for log values. It deliberately does not end in
// `unknown` — a trailing `unknown` collapses the whole union to "any value" and
// erases the serializable/redacted boundary. Arbitrary inputs are funnelled
// through `normalizeLogValue` (utils/normalize.ts) before they become a
// `LogValue`. `Error` is included for structured error logging.
export type LogValue = string | number | boolean | null | undefined | Error | LogValue[] | { [key: string]: LogValue };

export type LogMessage = string | number | boolean | Record<string, LogValue>;

export type LogMetadata = Record<string, LogValue> | Error | LogValue;

export type CustomProps = Record<string, LogValue>;

export type LogFuncWithProps = (
	type: LogType,
	level: LogLevel,
	injectedProps: CommonProps,
	message: LogMessage,
	...optionalParams: LogMetadata[]
) => void;

// The public logging boundary accepts arbitrary caller values; LogRouter normalizes
// each through `normalizeLogValue` into the closed `LogValue` before it is stored/forwarded.
export type LogFunc = (type: LogType, message: LogMessage, ...optionalParams: unknown[]) => void;

export type LogEntryFunc = (entry: LogEntry) => void;

export type MetricType = BaseMetric & MetricValues;

type BaseMetric = {
	name: string;
};

type MetricValues = Partial<Record<MetricUnit, number>>;

enum MetricUnit {
	latency_ms = "latency_ms",
	jitter_ms = "jitter_ms",
	elapsed_time_s = "elapsed_time_s",
	elapsed_time_ms = "elapsed_time_ms",
	upload_speed_mbps = "upload_speed_mbps",
	download_speed_mbps = "download_speed_mbps",
	count = "count",
}

// The NAMED keys below are the V1 ESTATE's correlation vocabulary (many apps,
// one shared dataset — the pandas_ prefix is the namespace). The v2 estate
// (api / vision-ai-v2 / pandas-hub) deliberately uses bare camelCase names
// (sessionId, launchId, identifier, …) flowing through the open CustomProps
// side.
export type CommonProps = {
	correlation_id?: string;
	pandas_session_id?: string;
	pandas_location_id?: string;
	pandas_identifier?: string;
	pandas_partner_code?: string;
	pandas_machine_id?: string;
	user_agent?: string;
	project?: string;
} & CustomProps;

export type LogEntry = CommonProps & {
	level: LogLevel | string;
	message?: string;
	metadata?: LogMetadata[];
	type: LogType;
	action?: string;
};

export type ILogForwarder = {
	logEntry: LogEntryFunc;
	log: (
		type: LogType,
		level: LogLevel,
		injectedProps: CommonProps,
		message: LogMessage,
		...metadata: LogMetadata[]
	) => void;
};

export type ILogger = {
	info: LogFunc;
	error: LogFunc;
	warning: LogFunc;
	debug: LogFunc;
	trace: LogFunc;
	fatal: LogFunc;
	log: (level: LogLevel, type: LogType, message: LogMessage, ...optionalParams: unknown[]) => void;
};

export type IRemoteLogForwarder = {
	/**
	 * Flush any buffered logs. Implementations should treat this as
	 * fire-and-forget: always resolve, never reject, and bound their own latency
	 * via `timeoutMs` when provided.
	 */
	flushLogs: (timeoutMs?: number) => Promise<void>;
};
